import { cx } from '@orn/shared-web/utils/cx';

import { AssistantBenchmarkRunStatus } from '../../__generated__/globalTypes';
import Button from '../../components/Button';

import {
  asBenchmarkVerdict,
  benchmarkStatusTone,
  githubCommitUrl,
  isTerminalBenchmarkStatus,
  shortSha,
  type BenchmarkStatusTone,
} from './assistantBenchmarkView';
import BenchmarkVerdictView from './BenchmarkVerdictView';

const TONE_CLASS: Record<BenchmarkStatusTone, string> = {
  neutral: 'text-fg-secondary',
  running: 'text-fg-brand-primary',
  pass: 'text-fg-success-primary',
  fail: 'text-fg-error-primary',
  inconclusive: 'text-fg-warning-primary',
  error: 'text-fg-error-primary',
};

interface IStatusBadgeProps {
  status: AssistantBenchmarkRunStatus;
}

/** A run's lifecycle status as a bordered pill, tinted by outcome. */
export const StatusBadge = ({ status }: IStatusBadgeProps) => (
  <span
    className={cx(
      'border-border-primary rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
      TONE_CLASS[benchmarkStatusTone(status)]
    )}
  >
    {status.toLowerCase()}
  </span>
);

interface ICommitLinkProps {
  sha: string;
}

/**
 * A short SHA linking to its GitHub commit page. Stops click propagation so that
 * inside a clickable run row, clicking the SHA navigates to GitHub instead of
 * opening the run's detail.
 */
export const CommitLink = ({ sha }: ICommitLinkProps) => (
  <a
    href={githubCommitUrl(sha)}
    target="_blank"
    rel="noopener noreferrer"
    title={sha}
    onClick={(event) => event.stopPropagation()}
    className="hover:text-fg-brand-primary font-mono underline decoration-dotted underline-offset-2"
  >
    {shortSha(sha)}
  </a>
);

/** The run fields this view renders — structurally satisfied by either run query's shape. */
export interface IBenchmarkRunSummary {
  baseSha: string;
  headSha: string;
  status: AssistantBenchmarkRunStatus;
  githubRunUrl?: string | null;
  verdict?: unknown;
}

interface IBenchmarkRunViewProps {
  /** `null` while the first fetch is outstanding, or when no run exists. */
  run: IBenchmarkRunSummary | null;
  hasQueryError: boolean;
  /**
   * The host's own poll cadence, quoted back in the retry notice. Passed in rather
   * than assumed: the two hosts poll at different rates, and a view that guessed
   * would promise recovery timing it does not control.
   */
  pollIntervalMs: number;
  /** Renders a Close control when the host is dismissible. */
  onClose?: () => void;
}

/**
 * One benchmark run: its commit pair, status, GitHub link, and — once terminal —
 * the full verdict.
 *
 * Takes run data rather than a run id because its two hosts locate a run
 * differently: the Benchmarks tool by id from the runs table, the release tool by
 * the open draft's target commit. Each owns its own query and polling; this
 * renders whatever it's handed.
 */
const BenchmarkRunView = ({ run, hasQueryError, pollIntervalMs, onClose }: IBenchmarkRunViewProps) => {
  const status = run?.status ?? null;
  const verdict = asBenchmarkVerdict(run?.verdict);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs">
            {run ? (
              <>
                <CommitLink sha={run.baseSha} /> → <CommitLink sha={run.headSha} />
              </>
            ) : (
              '…'
            )}
          </span>
          {run && <StatusBadge status={run.status} />}
        </div>
        <div className="flex items-center gap-3">
          {run?.githubRunUrl != null && (
            <a
              href={run.githubRunUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-fg-brand-primary text-xs underline"
            >
              GitHub run ↗
            </a>
          )}
          {onClose && (
            <Button color="secondary" size="xs" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>

      {hasQueryError && (status === null || !isTerminalBenchmarkStatus(status)) ? (
        // The reconcile poll is idempotent, so keep polling on a transient
        // GitHub/network error and let it self-heal — but surface the failure
        // rather than leave a misleading "Loading…" / "Polling…".
        <span className="text-fg-error-primary text-xs">
          Couldn't reach the server — retrying every {Math.round(pollIntervalMs / 1000)}s.
        </span>
      ) : run === null ? (
        <span className="text-fg-secondary text-xs">Loading…</span>
      ) : status !== null && !isTerminalBenchmarkStatus(status) ? (
        <span className="text-fg-secondary text-xs">Running the ladder — this takes a while. Polling…</span>
      ) : run.status === AssistantBenchmarkRunStatus.Errored ? (
        <span className="text-fg-error-primary text-xs">Errored — no verdict was produced. Check the GitHub run.</span>
      ) : verdict === null ? (
        <span className="text-fg-secondary text-xs">Settled with no verdict payload.</span>
      ) : (
        <BenchmarkVerdictView verdict={verdict} />
      )}
    </div>
  );
};

export default BenchmarkRunView;
